import * as THREE from "three";
import { RAMP_LENGTH, RAMP_SLOPE, ROOF_HEIGHT } from "../../data/entities";
import { FACE, G, Kit, ethMark, hdr, instance, once, profileSlab } from "./kit";
import { blobShadow } from "./fx";
import { addWheel } from "./parts";
import { canvasTex } from "./tex";

/**
 * The ramp trailer: a yellow steel deck rising from the road to the truck roof over RAMP_SLOPE (8.6 m), then flat to
 * RAMP_LENGTH (12 m); blue side plates with lightening holes, rails, marker lights, wheels under the high end.
 */

const TOP = ROOF_HEIGHT;
const HALF_W = 1.65;
const THICK = 0.26;                      // vertical thickness of the deck plate

/** Deck paint: yellow, black chevrons pointing up the slope, a green arrow; the bottom edge a hazard band. */
const deckTex = (slope: boolean): THREE.Texture => canvasTex(`ramp-deck:${slope}`, 256, slope ? 1024 : 384, (ctx, w, h) => {
  ctx.fillStyle = "#ffc21a";
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = "#17181f";
  const pitch = 128;
  for (let y = -pitch; y < h + pitch; y += pitch) {
    ctx.beginPath();
    ctx.moveTo(18, y + 70); ctx.lineTo(w / 2, y + 8); ctx.lineTo(w - 18, y + 70); ctx.lineTo(w - 18, y + 112); ctx.lineTo(w / 2, y + 50); ctx.lineTo(18, y + 112);
    ctx.closePath();
    ctx.fill();
  }
  // side edge lines
  ctx.fillRect(0, 0, 10, h);
  ctx.fillRect(w - 10, 0, 10, h);
  if (!slope) return;
  // a green arrow in the middle of the slope
  ctx.fillStyle = "#14f195";
  ctx.strokeStyle = "#0b3d27";
  ctx.lineWidth = 6;
  ctx.beginPath();
  const cy = h * 0.42;
  ctx.moveTo(w / 2, cy - 80); ctx.lineTo(w / 2 + 70, cy); ctx.lineTo(w / 2 + 26, cy); ctx.lineTo(w / 2 + 26, cy + 90); ctx.lineTo(w / 2 - 26, cy + 90); ctx.lineTo(w / 2 - 26, cy); ctx.lineTo(w / 2 - 70, cy);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // the lip: hazard stripes at the very bottom
  ctx.save();
  ctx.beginPath(); ctx.rect(0, h - 56, w, 56); ctx.clip();
  ctx.fillStyle = "#17181f"; ctx.fillRect(0, h - 56, w, 56);
  ctx.fillStyle = "#ffc21a";
  for (let x = -60; x < w + 60; x += 40) { ctx.beginPath(); ctx.moveTo(x, h); ctx.lineTo(x + 20, h); ctx.lineTo(x + 76, h - 56); ctx.lineTo(x + 56, h - 56); ctx.closePath(); ctx.fill(); }
  ctx.restore();
});

/** Diamond tread plate (normal map, tiles). */
const treadTex = (): THREE.Texture => canvasTex("tread", 64, 64, (ctx, w, h) => {
  ctx.fillStyle = "rgb(128,128,255)";
  ctx.fillRect(0, 0, w, h);
  const bump = (cx: number, cy: number, angle: number) => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.fillStyle = "rgb(128,168,235)"; ctx.fillRect(-11, -4, 22, 4);
    ctx.fillStyle = "rgb(128,88,235)"; ctx.fillRect(-11, 0, 22, 4);
    ctx.restore();
  };
  bump(16, 16, Math.PI / 4); bump(48, 48, Math.PI / 4); bump(48, 16, -Math.PI / 4); bump(16, 48, -Math.PI / 4);
}, { repeat: true, linear: true });

const deckMaterial = (slope: boolean): THREE.MeshStandardMaterial => once(`mat:rampdeck:${slope}`, () => {
  const normal = treadTex().clone();
  normal.wrapS = normal.wrapT = THREE.RepeatWrapping;
  normal.repeat.set(5, slope ? 22 : 8);
  normal.needsUpdate = true;
  return new THREE.MeshStandardMaterial({ map: deckTex(slope), normalMap: normal, normalScale: new THREE.Vector2(0.8, 0.8), roughness: 0.55, metalness: 0.04, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
});

/**
 * `buildRamp()` → origin = centre of the 12 m footprint, y = 0 on the road; the slope starts on the road at
 * z = −6 (towards the player) and reaches ROOF_HEIGHT at z = −6 + 8.6, flat to z = +6. ~1.9k triangles.
 */
export function buildRamp(): THREE.Group {
  return instance("ramp", () => {
    const L = RAMP_LENGTH, S = RAMP_SLOPE;
    const z0 = -L / 2, z1 = z0 + S, z2 = L / 2;
    const tan = TOP / S;
    const kit = new Kit();
    // the deck plate (its top is exactly the drivable surface)
    const under0 = z0 + THICK / tan;
    kit.add(profileSlab([[z0, 0], [z1, TOP], [z2, TOP], [z2, TOP - THICK], [z1, TOP - THICK], [under0, 0]], HALF_W * 2 - 0.1, 0.05, { bevelSegments: 1 }), "paint", 0xf0b412);
    // painted, tread-plated top surfaces
    const slopeLen = Math.hypot(S, TOP);
    const angle = Math.atan2(TOP, S);
    const deckW = HALF_W * 2 - 0.36;
    // v runs up the slope (chevrons point up it), u reads left-to-right from the oncoming camera
    kit.add(G.plane(deckW, slopeLen), deckMaterial(true), 0xffffff, { p: [0, TOP / 2 + 0.006 * Math.cos(angle), z0 + S / 2 - 0.006 * Math.sin(angle)], r: [-Math.PI / 2 - angle, 0, Math.PI] });
    kit.add(G.plane(deckW, z2 - z1), deckMaterial(false), 0xffffff, { p: [0, TOP + 0.006, (z1 + z2) / 2], r: FACE.up });

    // side plates: under the deck, with lightening holes, a wheel opening at the back
    const plate = new THREE.Shape();
    const base = 0.14;
    const zStart = z0 + (base + THICK) / tan;
    plate.moveTo(zStart, base);
    plate.lineTo(1.7, base);
    plate.lineTo(1.7, 1.2);
    plate.lineTo(z2 - 0.1, 1.2);
    plate.lineTo(z2 - 0.1, TOP - THICK);
    plate.lineTo(z1, TOP - THICK);
    plate.closePath();
    for (const [cz, cy, r] of [[-1.7, 1.25, 0.55], [0.35, 2.25, 0.82]] as [number, number, number][]) {
      const hole = new THREE.Path();
      hole.absarc(cz, cy, r, 0, Math.PI * 2, true);
      plate.holes.push(hole);
    }
    const plateGeo = once("g:rampplate", () => {
      const g = new THREE.ExtrudeGeometry(plate, { depth: 0.08, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.02, bevelOffset: -0.02, bevelSegments: 1, curveSegments: 14 });
      g.translate(0, 0, -0.04);
      g.rotateY(-Math.PI / 2);
      return g;
    });
    for (const x of [-1, 1]) kit.add(plateGeo, "gloss", 0x2f6fe4, { p: [x * (HALF_W - 0.1), 0, 0] });
    // inner bulkheads seen through the holes, cross beams, a back wall
    for (const z of [-2.6, -0.4, 1.7]) {
      const yTop = Math.min(TOP - THICK, (z - z0) * tan - THICK);
      if (yTop > 0.4) kit.add(G.box(HALF_W * 2 - 0.3, yTop - 0.14, 0.12), "satin", 0x1f3f8a, { p: [0, 0.14 + (yTop - 0.14) / 2, z] });
    }
    kit.add(G.box(HALF_W * 2 - 0.2, TOP - THICK - 1.2, 0.1), "gloss", 0x2f6fe4, { p: [0, 1.2 + (TOP - THICK - 1.2) / 2, z2 - 0.1] });

    // rails along both edges: posts and a top rail following the deck
    for (const x of [-1, 1]) {
      const rx = x * (HALF_W - 0.06);
      kit.add(G.box(0.07, 0.07, slopeLen), "paint", 0xffffff, { p: [rx, TOP / 2 + 0.58 / Math.cos(angle), z0 + S / 2], r: [-angle, 0, 0] });
      kit.add(G.box(0.07, 0.07, z2 - z1 + 0.04), "paint", 0xffffff, { p: [rx, TOP + 0.58, (z1 + z2) / 2] });
      for (let i = 1; i <= 7; i++) {
        const z = z0 + i * 1.5;
        const y = z < z1 ? (z - z0) * tan : TOP;
        kit.add(G.box(0.06, 0.6, 0.06), "paint", 0xffffff, { p: [rx, y + 0.3, z] });
      }
      // a hazard stripe along the slope edge of the side plate, amber marker lights
      kit.add(G.box(0.04, 0.16, slopeLen - 0.4), "paint", 0x17181f, { p: [x * (HALF_W - 0.04), TOP / 2 - 0.28, z0 + S / 2 + 0.25], r: [-angle, 0, 0] });
      for (const z of [-3.2, -0.6, 2.0, 4.5]) {
        const y = Math.min(TOP - THICK - 0.18, (z - z0) * tan - THICK - 0.18);
        kit.add(G.box(0.03, 0.12, 0.24), "glow", hdr(0xffa21f, 1.5), { p: [x * (HALF_W - 0.03), y, z] });
      }
      // the ETH crystal on the high end's side
      kit.add(ethMark(1.9, 0.09), "paint", 0xffffff, { p: [x * (HALF_W - 0.06), 3.25, 3.9], r: x > 0 ? FACE.left : FACE.right });
    }
    // chassis and wheels under the high end
    for (const x of [-0.6, 0.6]) kit.add(G.box(0.2, 0.28, z2 - 1.4), "satin", 0x24252c, { p: [x, 1.05, (1.4 + z2) / 2 - 0.7] });
    for (const z of [2.9, 4.6]) {
      kit.add(G.cyl(0.08, 0.08, 2.3, 8, true), "satin", 0x24252c, { p: [0, 0.52, z], r: [0, 0, Math.PI / 2] });
      for (const x of [-1, 1]) addWheel(kit, [x * 1.3, 0.52, z], 0.52, 0.4, x as 1 | -1);
    }
    // tail lights at the back
    for (const x of [-1.25, 1.25]) kit.add(G.box(0.4, 0.18, 0.05), "glow", hdr(0xff1e30, 1.5), { p: [x, 1.0, z2 + 0.01] });
    const root = new THREE.Group();
    root.name = "ramp";
    root.add(kit.build({ receive: true }), blobShadow(4.4, L + 1));
    return root;
  });
}
